/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../controls/Button';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import { CircleStackIcon, ChartBarSquareIcon, ListBulletIcon } from '@heroicons/react/24/outline';

interface EventHeaderProps {
  eventName: string;
  eventSlug: string;
  eventLocation: string;
  formattedStartDate: string;
  activeTab?: 'detail' | 'preparation' | 'insights';
  titleSuffix?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export default function EventHeader({
  eventName,
  eventSlug,
  eventLocation,
  formattedStartDate,
  activeTab = 'detail',
  titleSuffix,
  headerActions,
}: EventHeaderProps) {
  const navigate = useNavigate();

  return (
    <header className="relative isolate pt-10 bg-gradient-to-t from-dataops-process-cyan/20 to-white/0 to-50%">
      <PageCenteredLayout>
        <div className="mx-auto flex items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
          <div className="mx-auto flex flex-col items-start text-left gap-y-1 lg:mx-0 lg:max-w-none">
            <h1 className="text-xl font-bold text-dataops-yale-blue">
              {eventName}
              {titleSuffix && titleSuffix}
            </h1>
            <p className="text-base text-gray-600">{formattedStartDate}</p>
            <p className="text-sm text-gray-500">{eventLocation}</p>
          </div>
          {headerActions && <div>{headerActions}</div>}
        </div>
        <div id="event-tabs" className="flex absolute bottom-0 h-10 overflow-hidden z-10">
          <Button
            size="large"
            intent="custom"
            className={`${
              activeTab === 'detail'
                ? 'bg-white text-dataops-yale-blue'
                : 'transition-all bg-dataops-platinum-gray hover:bg-dataops-process-cyan/30 text-dataops-rich-black hover:top-0'
            } rounded-tl-sm rounded-none flex-col top-1`}
            disabled={activeTab === 'detail'}
            onClick={() => {
              void navigate(`/event-deployments/${eventSlug}`, { viewTransition: true });
            }}
          >
            <div className="flex items-center justify-center gap-1">
              <ListBulletIcon className="h-4 w-4" />
              <span>Detail</span>
            </div>
          </Button>
          <Button
            size="large"
            intent="custom"
            className={`${
              activeTab === 'preparation'
                ? 'bg-white text-dataops-yale-blue'
                : 'transition-all bg-dataops-platinum-gray hover:bg-dataops-process-cyan/30 text-dataops-rich-black hover:top-0'
            } rounded-none flex-col top-1`}
            disabled={activeTab === 'preparation'}
            onClick={() => {
              void navigate(`/event-deployments/${eventSlug}/preparation`, { viewTransition: true });
            }}
          >
            <div className="flex items-center justify-center gap-1">
              <CircleStackIcon className="h-4 w-4" />
              <span>Preparation</span>
            </div>
          </Button>
          <Button
            size="large"
            intent="custom"
            className={`${
              activeTab === 'insights'
                ? 'bg-white text-dataops-yale-blue'
                : 'transition-all bg-dataops-platinum-gray hover:bg-dataops-process-cyan/30 text-dataops-rich-black hover:top-0'
            } rounded-tr-sm rounded-none flex-col top-1`}
            disabled={activeTab === 'insights'}
            onClick={() => {
              void navigate(`/event-deployments/${eventSlug}/insights`, { viewTransition: true });
            }}
          >
            <div className="flex items-center justify-center gap-1">
              <ChartBarSquareIcon className="h-4 w-4" />
              <span>Insights</span>
            </div>
          </Button>
        </div>
      </PageCenteredLayout>
    </header>
  );
}
